import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { config } from '../../config';

export interface ContactStage {
  _id: string;
  name: string;
  description?: string;
  order: number;
  isActive: boolean;
  company_id: string;
  company?: {
    id: string;
    name: string;
  };
  created_by: {
    _id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  last_modified_by?: {
    _id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ContactStageCreateRequest {
  name: string;
  description?: string;
  order?: number;
}

export interface ContactStageUpdateRequest {
  name?: string;
  description?: string;
  order?: number;
}

export interface ContactStageResponse {
  success: boolean;
  message?: string;
  data?: ContactStage | ContactStage[];
}

@Injectable({
  providedIn: 'root'
})
export class ContactStageService {
  private apiUrl = `${config.apiUrl}/api/contact-stages`;
  private stagesSubject = new BehaviorSubject<ContactStage[]>([]);
  public stages$ = this.stagesSubject.asObservable();

  constructor(private http: HttpClient) {}

  getStages(): Observable<ContactStageResponse> {
    return this.http.get<ContactStageResponse>(this.apiUrl).pipe(
      tap(response => {
        if (response.success && Array.isArray(response.data)) {
          this.stagesSubject.next(response.data);
        }
      })
    );
  }

  createStage(stage: ContactStageCreateRequest): Observable<ContactStageResponse> {
    return this.http.post<ContactStageResponse>(this.apiUrl, stage).pipe(
      tap(response => {
        if (response.success && response.data) {
          const currentStages = this.stagesSubject.value;
          this.stagesSubject.next([...currentStages, response.data as ContactStage]);
        }
      })
    );
  }

  updateStage(id: string, stage: ContactStageUpdateRequest): Observable<ContactStageResponse> {
    return this.http.put<ContactStageResponse>(`${this.apiUrl}/${id}`, stage).pipe(
      tap(response => {
        if (response.success && response.data) {
          const currentStages = this.stagesSubject.value;
          const updatedStages = currentStages.map(s => 
            s._id === id ? response.data as ContactStage : s
          );
          this.stagesSubject.next(updatedStages);
        }
      })
    );
  }

  deleteStage(id: string): Observable<ContactStageResponse> {
    return this.http.delete<ContactStageResponse>(`${this.apiUrl}/${id}`).pipe(
      tap(response => {
        if (response.success) {
          const currentStages = this.stagesSubject.value;
          const filteredStages = currentStages.filter(s => s._id !== id);
          this.stagesSubject.next(filteredStages);
        }
      })
    );
  }

  getStageById(id: string): ContactStage | undefined {
    return this.stagesSubject.value.find(stage => stage._id === id);
  }

  refreshStages(): void {
    this.getStages().subscribe();
  }
}